import { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE_CONFIG, VEHICLE_MODELS } from "@/constants";
import { useLanguage } from "@/contexts/language-context";
import { WhatsAppContact } from "@/components/sections/whatsapp-contact";
import { Helmet } from "react-helmet-async";

export default function ContactPage() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    model: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    alert(t("contact.form.success"));
    setFormData({
      name: "",
      phone: "",
      email: "",
      city: "",
      model: "",
      message: "",
    });
    setIsSubmitting(false);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      <Helmet>
        <title>聯絡我們 | 長安跨越 電動車 | 預約試駕</title>
        <meta name="description" content="聯絡 長安跨越 電動車，預約 長安跨越 V7-EV 試駕。香港觀塘展示廳，電話 +852-9323-9473。" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-6">{t("contact.title")}</h1>
              <p className="text-xl text-blue-100">
                {t("contact.subtitle")}
              </p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-8 rounded-2xl shadow-xl">
                  <h3 className="font-bold text-xl mb-4 text-center text-white">{t("whatsappContact.contactTitle")}</h3>
                  <div className="flex flex-col items-center">
                    <div className="relative w-56 h-56 mb-4 bg-white rounded-2xl p-3">
                      <img
                        src="/img/whatsapp二维码.png"
                        alt="WhatsApp QR Code"
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    <p className="text-sm text-white font-medium text-center mb-2">
                      {t("whatsappContact.scanQR")}
                    </p>
                    <p className="text-xs text-green-100 text-center">
                      {t("whatsappContact.quickInfo")}
                    </p>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">{t("contact.phone.title")}</h3>
                      <p className="text-gray-600">{SITE_CONFIG.links.phone}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {t("contact.phone.hours")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">{t("contact.email.title")}</h3>
                      <p className="text-gray-600 break-all">
                        {SITE_CONFIG.links.email}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {t("contact.email.response")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">{t("contact.address.title")}</h3>
                      <p className="text-gray-600">{t("footer.address")}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {t("contact.address.visit")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 rounded-2xl text-white">
                  <h3 className="font-semibold mb-4">{t("contact.social.title")}</h3>
                  <div className="space-y-2">
                    <p className="flex items-center gap-2">
                      <span className="font-medium">{t("contact.social.wechat")}</span>
                      <span>長安跨越</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="font-medium">{t("contact.social.weibo")}</span>
                      <span>@長安跨越EV</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="font-medium">{t("contact.social.tiktok")}</span>
                      <span>長安跨越EV</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white p-8 rounded-2xl shadow-lg">
                  <h2 className="text-2xl font-bold mb-6">{t("contact.form.title")}</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium mb-2"
                        >
                          {t("contact.form.name")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all"
                          placeholder={t("contact.form.namePlaceholder")}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium mb-2"
                        >
                          {t("contact.form.phone")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all"
                          placeholder={t("contact.form.phonePlaceholder")}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium mb-2"
                        >
                          {t("contact.form.email")}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all"
                          placeholder={t("contact.form.emailPlaceholder")}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="city"
                          className="block text-sm font-medium mb-2"
                        >
                          {t("contact.form.city")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          required
                          value={formData.city}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all"
                          placeholder={t("contact.form.cityPlaceholder")}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="model"
                        className="block text-sm font-medium mb-2"
                      >
                        {t("contact.form.model")}
                      </label>
                      <select
                        id="model"
                        name="model"
                        value={formData.model}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all"
                      >
                        <option value="">{t("contact.form.modelPlaceholder")}</option>
                        {VEHICLE_MODELS.map((vehicle) => (
                          <option key={vehicle.id} value={vehicle.slug}>
                            {vehicle.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium mb-2"
                      >
                        {t("contact.form.message")}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all resize-none"
                        placeholder={t("contact.form.messagePlaceholder")}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        t("contact.form.submitting")
                      ) : (
                        <>
                          <Send className="mr-2 w-5 h-5" />
                          {t("contact.form.submit")}
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <WhatsAppContact />
      </div>
    </>
  );
}
